import { useEffect, useState } from 'react';
import type { EditWorkerSkillEntryContext } from '@scale/genai-compass-shared/cards/edit_worker_skill_entry';
import { useCompassCard } from '@scale/compass-ui/store';
import { ColumnSubType } from '@scale/compass-shared/types';
import { WorkerSkillEntry } from '@scale/shared/defaults';

export const useEditWorkerSkillEntryCard = (index: number) => {
  const { card, updateCardContext, updateCardCanSubmit } = useCompassCard(index);
  const { retrievedOutput } = useCompassCard(index - 1);

  const columns = retrievedOutput?.response?.schema || [];
  const context = card.defaultContext as EditWorkerSkillEntryContext;
  const [numWorkers, setNumWorkers] = useState(0);

  const setSelectedColumnIdWithWorkerIds = (selectedColumnIdWithWorkerIds: string) => {
    const updatedContext: EditWorkerSkillEntryContext = {
      ...context,
      selectedColumnIdWithWorkerIds,
    };
    updateCardContext(updatedContext);

    if (!selectedColumnIdWithWorkerIds) {
      setNumWorkers(0);
      updateCardCanSubmit(false);
      return;
    }

    // only submit if there are workers selected, and either removeAllSkills is checked, or there are valid skill entries
    if (retrievedOutput?.response?.data) {
      const workerRows = retrievedOutput.response.data.filter(
        row => row[selectedColumnIdWithWorkerIds],
      );
      setNumWorkers(workerRows.length);
    }

    if (
      numWorkers > 0 &&
      ((context.skillEntries.length > 0 &&
        context.skillEntries.every(entry => entry.skill.id !== '0')) ||
        context.removeAllSkills)
    ) {
      updateCardCanSubmit(true);
    }
  };

  const setSkillEntries = (skillEntries: WorkerSkillEntry[]) => {
    const updatedContext: EditWorkerSkillEntryContext = {
      ...context,
      skillEntries,
    };
    updateCardContext(updatedContext);

    if (
      numWorkers > 0 &&
      skillEntries.length > 0 &&
      skillEntries.every(entry => entry.skill.id !== '0')
    ) {
      updateCardCanSubmit(true);
    } else {
      updateCardCanSubmit(false);
    }
  };

  const setRemoveAllSkills = (removeAllSkills: boolean) => {
    const updatedContext: EditWorkerSkillEntryContext = {
      ...context,
      removeAllSkills,
    };
    updateCardContext(updatedContext);

    if (numWorkers > 0 && removeAllSkills) {
      updateCardCanSubmit(true);
    } else {
      updateCardCanSubmit(false);
    }
  };

  const setOverrideReason = (overrideReason: string) => {
    const updatedContext: EditWorkerSkillEntryContext = {
      ...context,
      overrideReason,
    };
    updateCardContext(updatedContext);
  };

  // const updateContext = useCallback(
  //   (updates: Partial<EditWorkerSkillEntryContext>) => {
  //     if (!context) return;
  //     updateCardContext({ ...context, ...updates } as EditWorkerSkillEntryContext);
  //   },
  //   [context, updateCardContext],
  // );

  useEffect(() => {
    // Try to set column with worker IDs as default.
    for (const column of columns) {
      if (column.subType === ColumnSubType.WORKER_ID) {
        setSelectedColumnIdWithWorkerIds(column.id);
        break;
      }
    }
  }, [columns]);

  return {
    skillEntries: context.skillEntries,
    setSkillEntries,
    removeAllSkills: context.removeAllSkills,
    setRemoveAllSkills,
    selectedColumnIdWithWorkerIds: context.selectedColumnIdWithWorkerIds,
    setSelectedColumnIdWithWorkerIds,
    overrideReason: context.overrideReason,
    setOverrideReason,
    numWorkers,
    columns,
  };
};
